import React from 'react'
import { getPricing, type PricingResponse, type PricingType } from '../../api/pricing'

const DEFAULT_CURRENCY = 'EUR'
const STORAGE_KEY = 'rpa_pricing_currency'

const readStoredCurrency = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_CURRENCY
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? stored.toUpperCase() : DEFAULT_CURRENCY
  } catch {
    return DEFAULT_CURRENCY
  }
}

const storeCurrency = (currency: string) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, currency)
  } catch {
    // Ignore storage errors.
  }
}

export const usePricingData = (type: PricingType) => {
  const [currency, setCurrency] = React.useState<string>(readStoredCurrency())
  const [supportedCurrencies, setSupportedCurrencies] = React.useState<string[]>([])
  const [pricingBundle, setPricingBundle] = React.useState<PricingResponse | null>(null)
  const [pricing, setPricing] = React.useState<PricingResponse | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    storeCurrency(currency)
  }, [currency])

  const applyCurrency = React.useCallback((bundle: PricingResponse, nextCurrency: string) => {
    const pricesByCurrency = bundle.prices_by_currency ?? {}

    if (!Object.keys(pricesByCurrency).length) {
      return { ...bundle, currency: nextCurrency }
    }

    const pricesForCurrency = pricesByCurrency[nextCurrency] ?? {}
    const products = bundle.products.map((product) => {
      const price = pricesForCurrency[product.key] ?? null
      return {
        ...product,
        price,
        available: Boolean(price),
      }
    })

    return {
      ...bundle,
      currency: nextCurrency,
      products,
    }
  }, [])

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getPricing(type, currency, { allCurrencies: true })
      .then((response) => {
        if (cancelled) {
          return
        }

        const nextCurrency = response.currency || currency
        setPricingBundle(response)
        setSupportedCurrencies(response.supported_currencies ?? [])
        setPricing(applyCurrency(response, nextCurrency))

        if (nextCurrency !== currency) {
          setCurrency(nextCurrency)
          storeCurrency(nextCurrency)
        }
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        const message = err instanceof Error ? err.message : 'Unable to load pricing.'
        setError(message)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [applyCurrency, type])

  React.useEffect(() => {
    if (!pricingBundle) {
      return
    }

    setPricing(applyCurrency(pricingBundle, currency))
  }, [applyCurrency, currency, pricingBundle])

  return {
    currency,
    setCurrency,
    supportedCurrencies,
    pricing,
    loading,
    error,
  }
}
